/** biome-ignore-all lint/style/useNamingConvention: this is for envs specifically, ALL_CAPS_FORMAT is fine */

const DEFAULT_OAUTH_REDIRECT_DELAY = 1500;

/**
 * Fail hard when a required variable is missing. Values are used verbatim:
 * never trim or normalize them, and never fall back to another name or a
 * hardcoded default — a silent fallback hides a broken deploy.
 */
export function requireEnv(name: string, value: string | undefined): string {
	if (!value) {
		throw new Error(`${name} is required`);
	}
	return value;
}

const env = {
	// Public environment variables (available on client and server)
	// NOTE: NEXT_PUBLIC_SIGMA_CLIENT_ID removed - auth server uses direct login, not OAuth to itself
	NEXT_PUBLIC_BASE_URL: requireEnv(
		"NEXT_PUBLIC_BASE_URL",
		process.env.NEXT_PUBLIC_BASE_URL
	),
	NEXT_PUBLIC_WALLET_REMOTE_URL: requireEnv(
		"NEXT_PUBLIC_WALLET_REMOTE_URL",
		process.env.NEXT_PUBLIC_WALLET_REMOTE_URL
	),
	NEXT_PUBLIC_AUTH_EMAIL: requireEnv(
		"NEXT_PUBLIC_AUTH_EMAIL",
		process.env.NEXT_PUBLIC_AUTH_EMAIL
	),
	NEXT_PUBLIC_DROPLIT_API_URL: requireEnv(
		"NEXT_PUBLIC_DROPLIT_API_URL",
		process.env.NEXT_PUBLIC_DROPLIT_API_URL
	),
	NEXT_PUBLIC_DROPLIT_NAME: requireEnv(
		"NEXT_PUBLIC_DROPLIT_NAME",
		process.env.NEXT_PUBLIC_DROPLIT_NAME
	),
	NEXT_PUBLIC_OAUTH_REDIRECT_DELAY: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_DELAY
		? Number.parseInt(process.env.NEXT_PUBLIC_OAUTH_REDIRECT_DELAY, 10)
		: DEFAULT_OAUTH_REDIRECT_DELAY,

	// Server-side environment variables (only available on server)
	POSTGRES_URL: process.env.POSTGRES_URL as string,
	BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET as string,
	BETTER_AUTH_URL: process.env.BETTER_AUTH_URL as string,
	KV_REST_API_URL: process.env.KV_REST_API_URL as string,
	KV_REST_API_TOKEN: process.env.KV_REST_API_TOKEN as string,
	RESEND_API_KEY: process.env.RESEND_API_KEY as string,

	// GitHub OAuth (optional - for account linking)
	GITHUB_CLIENT_ID: process.env.GITHUB_CLIENT_ID as string,
	GITHUB_CLIENT_SECRET: process.env.GITHUB_CLIENT_SECRET as string,

	// Google OAuth/OIDC (optional, but ID and secret must be configured together)
	// OAuth client IDs are public identifiers. Reuse the public value as the
	// single availability signal so client UI cannot advertise an unconfigured
	// provider while the server uses a different value.
	NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env
		.NEXT_PUBLIC_GOOGLE_CLIENT_ID as string,
	GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET as string,

	// Apple OAuth (optional - for account linking)
	APPLE_CLIENT_ID: process.env.APPLE_CLIENT_ID as string,
	APPLE_CLIENT_SECRET: process.env.APPLE_CLIENT_SECRET as string,

	// X OAuth - requires X_CLIENT_ID and X_CLIENT_SECRET from developer.x.com
	X_CLIENT_ID: process.env.X_CLIENT_ID as string,
	X_CLIENT_SECRET: process.env.X_CLIENT_SECRET as string,

	// Discord OAuth - requires DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET from discord.com/developers
	DISCORD_CLIENT_ID: process.env.DISCORD_CLIENT_ID as string,
	DISCORD_CLIENT_SECRET: process.env.DISCORD_CLIENT_SECRET as string,

	// Owner configuration - BAP Identity Keys of service owners
	OWNER_BAP_IDS: process.env.OWNER_BAP_IDS as string,

	// Ecosystem domain for the BRC-169 section 5.5 alias claim (admin
	// alias-claim console). Optional: when absent the console defaults to the
	// canonical ecosystem domain. Never trimmed, never reinterpreted.
	SIGMA_ECOSYSTEM_DOMAIN: process.env.SIGMA_ECOSYSTEM_DOMAIN as string,

	// BRC certifier identity (required for production certificate operations)
	CERTIFIER_WIF: process.env.CERTIFIER_WIF as string,
	// Opaque rotation version, required in production. The public key is
	// derived from CERTIFIER_WIF; it is never configured separately.
	CERTIFIER_ACTIVE_KEY_VERSION: process.env
		.CERTIFIER_ACTIVE_KEY_VERSION as string,
	// BAP identity whose stored profile supplies the certifier's public display
	// name and avatar for the overlay registrant token. Required only when an
	// owner publishes that token; never used to derive keys.
	CERTIFIER_BAP_ID: process.env.CERTIFIER_BAP_ID as string,

	// Phone ownership verification (optional until the feature is used)
	PHONE_VERIFICATION_PROVIDER: process.env
		.PHONE_VERIFICATION_PROVIDER as string,
	PHONE_VERIFICATION_HASH_SECRET: process.env
		.PHONE_VERIFICATION_HASH_SECRET as string,
	TWILIO_ACCOUNT_SID: process.env.TWILIO_ACCOUNT_SID as string,
	TWILIO_AUTH_TOKEN: process.env.TWILIO_AUTH_TOKEN as string,
	TWILIO_FROM_NUMBER: process.env.TWILIO_FROM_NUMBER as string,
};

// NOTE: Server-side validation is now handled gracefully by /api/setup/status
// Missing env vars will show a nice setup UI instead of crashing

/**
 * Shared production detection — single source of truth.
 * Checks BETTER_AUTH_URL, NODE_ENV, and VERCEL_ENV.
 */
export const isProduction =
	process.env.BETTER_AUTH_URL?.includes("sigmaidentity.com") ||
	process.env.NODE_ENV === "production" ||
	process.env.VERCEL_ENV === "production";

export { env };
